import { useNavigate } from 'react-router-dom';
import { withLoading } from '@front-activerse/common-components';
import { formatIsoDate } from '@front-activerse/common-components';
import { CopyConnectorApollo } from '@front-activerse/common-components';
import { SetMealEatenConnectorApollo } from '@front-activerse/common-components';
import { AddMealToFavoritesConnectorApollo } from '@front-activerse/common-components';
import { RemoveMealFromFavoritesConnectorApollo } from '@front-activerse/common-components';
import { MealReplacementsConnectorApollo } from '@front-activerse/common-components';
import Timeline from '../DayView/components/DayPlanTimeline';
import * as PATHS from '../MealPlanPaths';
import * as types from '../MealPlanTypes';

import DayViewContainer from '../DayView/DayViewContainer';
import DayPlanIndexPlaceholder from './DayPlanIndexPlaceholder';

import CurrentWeightRequired from '../OtherPages/CurrentWeightRequired';
import GoalLost from '../OtherPages/GoalLost';
import GoalReached from '../OtherPages/GoalReached';
import NoDiet from '../OtherPages/NoDiet';
import DietInFuture from '../OtherPages/DietInFuture';
import PaymentPending from '../OtherPages/PaymentPending';

import UnauthorizedException from '../../Unauthorized/UnauthorizedException';

const DayPlanIndexContainer = ({
  date,
  dietAvailabilityDate,
  dailyDietPlan,
  availableDays,
  categories,
  goal,
  isHolidayMenu,
  isTimeToNagForCurrentMeasurement,
  alreadyCopied,
  prefetchSurroundingDays,
  prefetchMealDetails,
  dietId,
  recipeNotesForNextDay,
  intermittentFasting,
  loading2,
  token,
  description,
  setUserFlagData,
  dietPlanIntroSeen,
  mealPlanNavigation,
  notificationData,
  closedNotifications,
  userId,
  getClosedNotificationsValues,
  markNotificationAsClosed,
  loadingMealPlanNotification,
  trackerArticleId,
}) => {
  const navigate = useNavigate();

  const renderTimeline = () => (
    <Timeline availableDays={availableDays} date={date} loading={loading2} />
  );

  const type = dailyDietPlan.__typename;

  if (type === types.DAY_PLAN) {
    return (
      <MealReplacementsConnectorApollo date={dailyDietPlan.date}>
        {({ loadDayPlanMealReplacements }) => (
          <CopyConnectorApollo date={dailyDietPlan.date}>
            {({ copyDayPlan }) => (
              <SetMealEatenConnectorApollo date={dailyDietPlan.date}>
                {({ setMealEaten }) => (
                  <AddMealToFavoritesConnectorApollo date={dailyDietPlan.date}>
                    {({ addMealToFavorites }) => (
                      <RemoveMealFromFavoritesConnectorApollo date={dailyDietPlan.date}>
                        {({ removeMealFromFavorites }) => (
                          <DayViewContainer
                            availableDays={availableDays}
                            dailyDietPlan={dailyDietPlan}
                            categories={categories}
                            renderTimeline={() => renderTimeline()}
                            goal={goal}
                            isHolidayMenu={isHolidayMenu}
                            isTimeToNagForCurrentMeasurement={isTimeToNagForCurrentMeasurement}
                            copyDayPlan={copyDayPlan}
                            alreadyCopied={alreadyCopied}
                            loadDayPlanMealReplacements={loadDayPlanMealReplacements}
                            prefetchSurroundingDays={prefetchSurroundingDays}
                            prefetchMealDetails={prefetchMealDetails}
                            setMealEaten={setMealEaten}
                            addMealToFavorites={addMealToFavorites}
                            removeMealFromFavorites={removeMealFromFavorites}
                            dietId={dietId}
                            recipeNotesForNextDay={recipeNotesForNextDay}
                            intermittentFasting={intermittentFasting}
                            setUserFlagData={setUserFlagData}
                            dietPlanIntroSeen={dietPlanIntroSeen}
                            mealPlanNavigation={mealPlanNavigation}
                            mealPlanNotificationData={notificationData}
                            closedNotifications={closedNotifications}
                            userId={userId}
                            getClosedNotificationsValues={getClosedNotificationsValues}
                            markNotificationAsClosed={markNotificationAsClosed}
                            loadingMealPlanNotification={loadingMealPlanNotification}
                            trackerArticleId={trackerArticleId}
                          />
                        )}
                      </RemoveMealFromFavoritesConnectorApollo>
                    )}
                  </AddMealToFavoritesConnectorApollo>
                )}
              </SetMealEatenConnectorApollo>
            )}
          </CopyConnectorApollo>
        )}
      </MealReplacementsConnectorApollo>
    );
  }

  if (type === types.DAY_REDIRECT) {
    navigate(`${PATHS.PATH_DAY_PLAN_INDEX}/${formatIsoDate(dailyDietPlan.date)}`);
    return null;
  }

  if (type === types.CURRENT_WEIGHT_REQUIRED) {
    return <CurrentWeightRequired mealPlanNavigation={mealPlanNavigation} />;
  }

  if (type === types.GOAL_LOST) {
    return <GoalLost mealPlanNavigation={mealPlanNavigation} />;
  }

  if (type === types.GOAL_REACHED) {
    return <GoalReached mealPlanNavigation={mealPlanNavigation} />;
  }

  if (type === types.DIET_SETTINGS) {
    return <NoDiet mealPlanNavigation={mealPlanNavigation} />;
  }

  if (type === types.DIET_IN_FUTURE) {
    return (
      <DietInFuture
        date={dietAvailabilityDate}
        renderTimeline={() => renderTimeline()}
        mealPlanNavigation={mealPlanNavigation}
      />
    );
  }

  if (type === types.PAYMENT_PENDING) {
    return (
      <PaymentPending
        token={token}
        description={description}
        mealPlanNavigation={mealPlanNavigation}
      />
    );
  }

  if (type === types.UNAUTHORIZED) {
    return (
      <UnauthorizedException
        accessTo={dailyDietPlan.date}
        mealPlanNavigation={mealPlanNavigation}
      />
    );
  }

  throw new Error(`Unknown day plan type: '${type}'`);
};

const Placeholder = ({ mealPlanNavigation, availableDays, date, loading2 }) => (
  <DayPlanIndexPlaceholder
    mealPlanNavigation={mealPlanNavigation}
    availableDays={availableDays}
    date={date}
    loading2={loading2}
  />
);

export default withLoading(DayPlanIndexContainer, Placeholder);
